import React, { useCallback, useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { Button } from "./ui";
import LedgerRules from "./LedgerRules";
import CurrencidaStatements from "./CurrencidaStatements";
import { useApp } from "../context/AppContext";
import { tp, TL } from "../i18n";
import { Plus, Pencil, Trash2, Check, X, ExternalLink, Save, RefreshCw, SlidersHorizontal, Columns2 } from "lucide-react";

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
const money = (v) => num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERIODS = ["", "annual", "quarterly", "semiannual", "monthly", "custom"];
const TRIGGERS = [
  ["", "No auto-charge"],
  ["cash_same", "ATM — same network"],
  ["cash_unrelated", "ATM — unrelated"],
  ["cash_foreign", "ATM — foreign"],
  ["wire_in", "Wire — incoming"],
  ["wire_out", "Wire — outgoing"],
];
const PACKAGE_PERIODS = [["", "package…"], ["annual", "Annual"], ["semiannual", "Semiannual"],
  ["quarterly", "Quarterly"], ["monthly", "Monthly"], ["waived", "Waived or included"]];
const BANK_TRIGGERS = [["", "charge type…"], ["cash_same", "ATM — Same network"], ["cash_unrelated", "ATM — Unrelated"],
  ["cash_foreign", "ATM — Foreign"], ["cash_fx_fee", "Foreign Currency Fee"], ["cash_fx_atm", "Foreign Currency ATM Fee"],
  ["cash_disbursement", "Cash disbursement Fee"], ["wire_in", "Wire — In"], ["wire_out", "Wire — Out"]];
const LEVY_SECTIONS = [
  { key: "dr_below", top: "Debit", bot: "Under" },
  { key: "dr_above", top: "Debit", bot: "Over" },
  { key: "cr_below", top: "Credit", bot: "Under" },
  { key: "cr_above", top: "Credit", bot: "Over" },
];
const FEE_TYPES = ["Account maintenance fee", "Account Closing Fee", "Account Opening Fee", "Card Issuance Fee",
  "Card Maintenance Fee", "CBN Levy", "Insufficient Funds Fee", "Miscellaneous Fee", "OTP Levy", "OTP Maintenance",
  "Overdraft", "Overdraft Interest", "POS Levy", "SMS Delivery", "SMS Levy", "SMS Maintenance"];
const blank = { date: "", ref: "", description: "", desc_type: "", context_on: false, context_note: "", details_on: true,
  details: "", counterparty: "", debit: "", credit: "", charge: "", charge_period: "", reconciled: false, trigger: "",
  package_amount: "", package_period: "",
  crypto: false, crypto_direction: "buy", crypto_symbol: "", crypto_units: "", crypto_value: "" };

const field = "rounded-md border border-black/15 dark:border-white/15 bg-white/70 dark:bg-black/30 px-2 py-1 text-sm";

const FEE_FAMILIES = [["cash", "ATM"], ["wire", "Wires"], ["package", "Package"], ["transactional", "Txn"]];
const FEE_PERIODS = [["month", "This month"], ["quarter", "This quarter"], ["year", "This year"], ["all", "All time"]];
function feePeriodStart(period) {
  const n = new Date();
  if (period === "month") return new Date(n.getFullYear(), n.getMonth(), 1);
  if (period === "quarter") return new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3, 1);
  if (period === "year") return new Date(n.getFullYear(), 0, 1);
  return null;
}

export default function CurrencyLedger({ app = "currency", accountId, popout = false, regName = "" }) {
  const { lang } = useApp();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [openingEdit, setOpeningEdit] = useState(false);
  const [opening, setOpening] = useState("");
  const [busy, setBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [tileOpen, setTileOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [tileB, setTileB] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [paySource, setPaySource] = useState("");
  const [odLimit, setOdLimit] = useState("");
  const [odAuto, setOdAuto] = useState(false);
  const [odDate, setOdDate] = useState("");
  const [feePeriod, setFeePeriod] = useState("month");
  const [cryptoPx, setCryptoPx] = useState(null);
  const [descOptions, setDescOptions] = useState(() => {
    const BASE = ["Client Code", "Line 1 Note", "Interim", "Miscellaneous"];
    try { const c = JSON.parse(localStorage.getItem("fopfo_custom_fields") || "[]"); return [...BASE, ...c]; }
    catch { return BASE; }
  });
  const addDescOption = () => {
    const v = (window.prompt("Add a description category:") || "").trim();
    if (!v) return;
    let c = []; try { c = JSON.parse(localStorage.getItem("fopfo_custom_fields") || "[]"); } catch { c = []; }
    if (!c.includes(v)) c = [...c, v];
    localStorage.setItem("fopfo_custom_fields", JSON.stringify(c));
    setDescOptions(["Client Code", "Line 1 Note", "Interim", "Miscellaneous", ...c]);
    set("desc_type", v);
  };
  const isBox = app === "box" || app === "holdings";
  const [levy, setLevy] = useState(() => LEVY_SECTIONS.map(() => ({ on: false, item: "", amount: "", tax_label: "", tax_val: "", tax_mode: "amt", inhouse: "", entry: "" })));
  const setLevyCell = (i, k, v) => setLevy((xs) => xs.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const [sec5, setSec5] = useState({ fee: "", item_amount: "", tax_label: "", tax_val: "", tax_mode: "amt", date: "" });
  const [feeFilter, setFeeFilter] = useState("");
  const setSec5Cell = (k, v) => setSec5((s) => ({ ...s, [k]: v }));
  const BLANK_INT = { date: "", gross: "", pct: "", tax_gross: "", tax_pct: "" };
  const [intCharged, setIntCharged] = useState(BLANK_INT);
  const [intEarned, setIntEarned] = useState(BLANK_INT);
  const setIC = (k, v) => setIntCharged((s) => ({ ...s, [k]: v }));
  const setIE = (k, v) => setIntEarned((s) => ({ ...s, [k]: v }));

  const load = useCallback(() => {
    const q = regName ? `?name=${encodeURIComponent(regName)}` : "";
    client.get(`/ledger/${app}/${accountId}${q}`).then((r) => { setData(r.data); setOpening(String(r.data.account.opening_balance ?? 0)); })
      .catch((e) => setMsg(apiErr(e)));
  }, [app, accountId, regName]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!form.crypto || !form.crypto_symbol) { setCryptoPx(null); return; }
    const sym = form.crypto_symbol.toUpperCase();
    const t = setTimeout(() => {
      client.get(`/crypto/prices?symbols=${sym}`).then((r) => setCryptoPx(r.data?.[sym]?.usd ?? null)).catch(() => setCryptoPx(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.crypto, form.crypto_symbol]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => {
    setForm(blank); setEditId(null);
    setLevy(LEVY_SECTIONS.map(() => ({ on: false, item: "", amount: "", tax_label: "", tax_val: "", tax_mode: "amt", inhouse: "", entry: "" })));
    setSec5({ fee: "", item_amount: "", tax_label: "", tax_val: "", tax_mode: "amt", date: "" });
    setIntCharged(BLANK_INT); setIntEarned(BLANK_INT);
  };

  const save = async () => {
    setBusy(true); setMsg("");
    const payload = { ...form, debit: num(form.debit), credit: num(form.credit), charge: num(form.charge),
      package_amount: num(form.package_amount),
      crypto_units: num(form.crypto_units),
      levy: levy.filter((s) => s.on).map((s) => ({ ...s, amount: num(s.amount), tax_val: num(s.tax_val) })),
      sec5: sec5.fee ? { ...sec5, item_amount: num(sec5.item_amount), tax_val: num(sec5.tax_val) } : null };
    delete payload.crypto_value;
    delete payload.crypto_direction;
    if (form.crypto) {
      const val = num(form.crypto_value);
      payload.crypto = true;
      payload.crypto_symbol = (form.crypto_symbol || "").toUpperCase();
      payload.crypto_units = num(form.crypto_units);
      payload.debit = form.crypto_direction === "buy" ? val : 0;
      payload.credit = form.crypto_direction === "receive" ? val : 0;
      payload.charge = 0; payload.charge_period = ""; payload.trigger = "";
      if (!payload.description) payload.description = `${form.crypto_direction === "buy" ? "Buy with" : "Receive"} ${payload.crypto_units} ${payload.crypto_symbol}`.trim();
    }
    try {
      const { data: d } = editId
        ? await client.put(`/ledger/${app}/${accountId}/entries/${editId}`, payload)
        : await client.post(`/ledger/${app}/${accountId}/entries`, payload);
      setData(d); reset();
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };
  const edit = (e) => {
    setEditId(e.id);
    setForm({ ...blank, ...e, debit: e.debit || "", credit: e.credit || "", charge: e.charge || "",
      crypto_value: e.crypto ? (e.debit || e.credit || "") : "",
      crypto_direction: e.crypto ? (num(e.debit) > 0 ? "buy" : "receive") : "buy",
      crypto_units: e.crypto_units || "" });
  };
  const del = async (id) => {
    if (!window.confirm(tp("Delete this entry?", lang))) return;
    try { const { data: d } = await client.delete(`/ledger/${app}/${accountId}/entries/${id}`); setData(d); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const applyFee = async () => {
    if (!sec5.fee) { setMsg(tp("Choose a fee type first.", lang)); return; }
    try {
      const { data: d } = await client.post(`/ledger/${app}/${accountId}/fee`, {
        fee: sec5.fee, item_amount: num(sec5.item_amount), tax_label: sec5.tax_label,
        tax_val: num(sec5.tax_val), tax_mode: sec5.tax_mode, date: sec5.date });
      setData(d); setMsg(`${tp("Applied", lang)} ${sec5.fee}.`);
    } catch (e) { setMsg(apiErr(e)); }
  };
  const deleteAllFee = async () => {
    if (!sec5.fee) { setMsg(tp("Choose a fee type first.", lang)); return; }
    if (!window.confirm(tp("Delete ALL posted fees of this type on this account?", lang))) return;
    try {
      const { data: d } = await client.delete(`/ledger/${app}/${accountId}/fee`, { params: { fee: sec5.fee } });
      setData(d); setMsg(`${tp("Removed", lang)} ${d.deleted || 0} "${sec5.fee}" ${tp("fee line(s).", lang)}`);
    } catch (e) { setMsg(apiErr(e)); }
  };
  const findFee = () => setFeeFilter((f) => (f === sec5.fee ? "" : sec5.fee));
  const undoSec5 = () => setSec5({ fee: "", item_amount: "", tax_label: "", tax_val: "", tax_mode: "amt", date: "" });
  const applyInterest = async (kind) => {
    const b = kind === "earned" ? intEarned : intCharged;
    try {
      const { data: d } = await client.post(`/ledger/${app}/${accountId}/interest`, {
        kind, date: b.date, gross: num(b.gross), pct: num(b.pct), tax_gross: num(b.tax_gross), tax_pct: num(b.tax_pct) });
      setData(d); setMsg(kind === "earned" ? tp("Net interest earned added to balance.", lang) : tp("Gross interest charged applied to balance.", lang));
      if (kind === "earned") setIntEarned(BLANK_INT); else setIntCharged(BLANK_INT);
    } catch (e) { setMsg(apiErr(e)); }
  };
  const toggleReconcile = async (e) => {
    try { const { data: d } = await client.put(`/ledger/${app}/${accountId}/entries/${e.id}`, { ...e, reconciled: !e.reconciled }); setData(d); }
    catch (er) { setMsg(apiErr(er)); }
  };
  const saveOpening = async () => {
    try { const { data: d } = await client.put(`/ledger/${app}/${accountId}/opening`, { opening_balance: num(opening) }); setData(d); setOpeningEdit(false); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const openPay = async () => {
    setPayOpen((v) => !v);
    if (accounts.length === 0) {
      try { const { data: d } = await client.get(`/ledger/accounts`); setAccounts(d.accounts || []); }
      catch (e) { setMsg(apiErr(e)); }
    }
  };
  const payCard = async () => {
    const amt = num(payAmount);
    if (amt <= 0) { setMsg(tp("Enter a payment amount.", lang)); return; }
    try {
      const { data: d } = await client.post(`/ledger/${app}/${accountId}/pay`, {
        amount: amt, from_account_id: paySource || "", note: paySource ? "Card payment (from pool)" : "Card payment" });
      setData(d); setMsg(`${tp("Payment of", lang)} ${money(amt)} ${tp("applied.", lang)}`);
      setPayOpen(false); setPayAmount(""); setPaySource("");
    } catch (e) { setMsg(apiErr(e)); }
  };
  const applyOverdraft = async () => {
    try {
      const { data: d } = await client.post(`/ledger/${app}/${accountId}/overdraft`, { limit: num(odLimit), auto: odAuto });
      setData(d); setMsg(tp("Overdraft facility updated.", lang));
    } catch (e) { setMsg(apiErr(e)); }
  };
  const undoOverdraft = () => {
    setOdLimit(String(data?.account?.overdraft_limit || ""));
    setOdAuto(!!data?.account?.overdraft_auto);
  };
  useEffect(() => {
    if (data?.account) { setOdLimit(String(data.account.overdraft_limit || "")); setOdAuto(!!data.account.overdraft_auto); }
  }, [data?.account?.overdraft_limit, data?.account?.overdraft_auto]);
  const openTilePicker = async () => {
    setTileOpen((v) => !v);
    if (accounts.length === 0) {
      try { const { data: d } = await client.get(`/ledger/accounts`); setAccounts(d.accounts || []); }
      catch (e) { setMsg(apiErr(e)); }
    }
  };
  const launchTile = () => {
    if (!tileB) return;
    const url = `/ledger/tile?a=${app}:${accountId}&b=${encodeURIComponent(tileB)}`;
    const w = window.open(url, "_blank", "width=1400,height=900");
    if (!w) window.open(url, "_blank");
    setTileOpen(false);
  };

  if (!data) return <div className="p-4 text-sm opacity-60" data-testid="ledger-loading">{msg || tp("Loading register…", lang)}</div>;

  const feeStart = feePeriodStart(feePeriod);
  const fees = { cash: 0, wire: 0, package: 0, transactional: 0 };
  (data.entries || []).forEach((e) => {
    if (!e.auto || num(e.charge) <= 0) return;
    if (feeStart && e.date && new Date(e.date) < feeStart) return;
    if (fees[e.rule_type] !== undefined) fees[e.rule_type] += num(e.charge);
  });
  const feeTotal = fees.cash + fees.wire + fees.package + fees.transactional;

  const balNow = num(data.balance);
  // Base = flat amount + a % of balance; Tax = flat tax amount + a % of the base. Either, or both.
  const _effG = (b) => num(b.gross) + balNow * num(b.pct) / 100;
  const _effT = (b) => { const g = _effG(b); return num(b.tax_gross) + g * num(b.tax_pct) / 100; };
  const netEarned = _effG(intEarned) - _effT(intEarned);
  const grossCharged = _effG(intCharged) + _effT(intCharged);
  const acctKind = data.account.kind || "Deposit";
  const isCredit = acctKind !== "Deposit";
  const creditLimit = num(data.account.credit_limit);
  const owed = Math.max(0, -num(data.balance));
  const available = creditLimit + num(data.balance);
  const util = creditLimit > 0 ? (owed / creditLimit) * 100 : 0;
  const odLimitNum = num(odLimit);
  const odUsed = Math.max(0, -num(data.balance));
  const odNet = num(data.balance) + odLimitNum;
  const intRow = (kind, b, setB, total, totalLabel, btnLabel) => (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-black/10 p-2 dark:border-white/10" data-testid={`ledger-interest-${kind}`}>
      <span className="text-[11px] font-bold uppercase tracking-wide text-[var(--primary)]">{kind === "earned" ? tp("Interest Earned", lang) : tp("Interest Charged", lang)}</span>
      <input type="date" value={b.date} onChange={(e) => setB("date", e.target.value)} className={`${field} w-32`} data-testid={`int-${kind}-date`} />
      <input type="number" step="any" placeholder={tp("Base amount", lang)} value={b.gross} onChange={(e) => setB("gross", e.target.value)} className={`${field} w-40`} data-testid={`int-${kind}-gross`} />
      <input type="number" step="any" placeholder={tp("Base %", lang)} value={b.pct} onChange={(e) => setB("pct", e.target.value)} className={`${field} w-24`} data-testid={`int-${kind}-pct`} />
      <input type="number" step="any" placeholder={kind === "earned" ? tp("Tax withheld amt", lang) : tp("Tax added amt", lang)} value={b.tax_gross} onChange={(e) => setB("tax_gross", e.target.value)} className={`${field} w-40`} data-testid={`int-${kind}-tax-gross`} />
      <input type="number" step="any" placeholder={tp("Tax %", lang)} value={b.tax_pct} onChange={(e) => setB("tax_pct", e.target.value)} className={`${field} w-24`} data-testid={`int-${kind}-tax-pct`} />
      <span className="text-xs whitespace-nowrap"><span className="opacity-55">{totalLabel}:</span> <b data-testid={`int-${kind}-total`}>{money(total)}</b></span>
      <div className="ms-auto flex items-center gap-1">
        <button type="button" onClick={() => applyInterest(kind)} className="rounded bg-[var(--primary)] px-2 py-1 text-[10px] font-medium text-white" data-testid={`int-${kind}-apply`}>{btnLabel}</button>
        <button type="button" onClick={() => (kind === "earned" ? setIntEarned(BLANK_INT) : setIntCharged(BLANK_INT))} className="rounded border border-black/15 px-2 py-1 text-[10px] dark:border-white/20" data-testid={`int-${kind}-undo`}>{tp("Undo", lang)}</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid={`ledger-${accountId}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm">
          <span className="opacity-60">{tp("Opening balance:", lang)}</span>{" "}
          {openingEdit ? (
            <span className="inline-flex items-center gap-1">
              <input value={opening} onChange={(e) => setOpening(e.target.value)} className={`${field} w-52`} data-testid="ledger-opening-input" />
              <button onClick={saveOpening} data-testid="ledger-opening-save" className="text-[var(--primary)]"><Save size={14} /></button>
              <button onClick={() => { setOpeningEdit(false); setOpening(String(data.account.opening_balance)); }}><X size={14} /></button>
            </span>
          ) : (
            <button onClick={() => setOpeningEdit(true)} data-testid="ledger-opening-edit" className="font-medium underline decoration-dotted">{money(data.account.opening_balance)}</button>
          )}
        </div>
        <div className="text-sm"><span className="opacity-60">{tp("Debits:", lang)}</span> <b className="text-[#DA1E28]">{money(data.totals.debit)}</b></div>
        <div className="text-sm"><span className="opacity-60">{tp("Credits:", lang)}</span> <b className="text-[#0E9F6E]">{money(data.totals.credit)}</b></div>
        <div className="text-sm"><span className="opacity-60">{tp("Charges:", lang)}</span> <b>{money(data.totals.charge)}</b></div>
        {isCredit ? (
          <>
            <div className="text-sm"><span className="opacity-60">{tp("Owed:", lang)}</span> <b className="text-[#DA1E28]" data-testid="ledger-owed">{money(owed)}</b></div>
            <div className="text-sm"><span className="opacity-60">{tp("Available:", lang)}</span> <b className={available < 0 ? "text-[#DA1E28]" : "text-[#0E9F6E]"} data-testid="ledger-available">{money(available)}</b></div>
            <div className="text-sm"><span className="opacity-60">{tp("Limit:", lang)}</span> <b data-testid="ledger-limit">{money(creditLimit)}</b></div>
            <div className="text-sm"><span className="opacity-60">{tp("Utilisation:", lang)}</span> <b data-testid="ledger-util" className={util >= 90 ? "text-[#DA1E28]" : util >= 70 ? "text-[#C99415]" : ""}>{util.toFixed(1)}%</b></div>
            {num(data.account.min_payment) > 0 && (
              <div className="text-sm"><span className="opacity-60">{tp("Min payment:", lang)}</span> <b data-testid="ledger-min-payment">{money(data.account.min_payment)}</b>{data.account.payment_due && <span className="opacity-60"> {tp("by", lang)} <b data-testid="ledger-payment-due">{data.account.payment_due}</b></span>}</div>
            )}
            <button onClick={openPay} data-testid="ledger-pay-open" className="rounded-full bg-[var(--primary)] px-3 py-1 text-xs font-medium text-white lift">{tp("Pay card", lang)}</button>
          </>
        ) : (
          <div className="text-sm"><span className="opacity-60">{tp("Balance:", lang)}</span> <b data-testid="ledger-balance">{money(data.balance)}</b></div>
        )}
        <div className="ms-auto flex items-center gap-2">
          <button onClick={load} title={tp("Refresh", lang)} data-testid="ledger-refresh" className="opacity-60 hover:opacity-100"><RefreshCw size={15} /></button>
          <Button variant="subtle" data-testid="ledger-rules-open" onClick={() => setRulesOpen(true)}>
            <SlidersHorizontal size={14} /> Rules
          </Button>
          {!popout && (
            <>
              <div className="relative">
                <Button variant="subtle" data-testid="ledger-tile-open" onClick={openTilePicker}>
                  <Columns2 size={14} /> Pop out &amp; tile
                </Button>
                {tileOpen && (
                  <div className="absolute right-0 z-50 mt-1 w-72 rounded-lg border border-black/10 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-[#0b1020]" data-testid="ledger-tile-picker">
                    <div className="mb-1 text-xs opacity-60">{tp("Open this register beside…", lang)}</div>
                    <select value={tileB} onChange={(e) => setTileB(e.target.value)} className={`${field} w-full`} data-testid="ledger-tile-select">
                      <option value="">{tp("Select an account…", lang)}</option>
                      {accounts.filter((a) => !(a.app === app && a.id === accountId)).map((a) => (
                        <option key={`${a.app}:${a.id}`} value={`${a.app}:${a.id}`}>
                          {a.app === "holdings" ? "BoxAlong" : a.app === "box" ? "BoxAlong register" : "Currencida"}: {a.name}
                        </option>
                      ))}
                    </select>
                    <div className="mt-2 flex justify-end">
                      <Button onClick={launchTile} disabled={!tileB} data-testid="ledger-tile-go"><Columns2 size={14} /> {tp("Open side by side", lang)}</Button>
                    </div>
                  </div>
                )}
              </div>
              <Button variant="subtle" data-testid="ledger-popout"
                onClick={() => { const w = window.open(`/ledger/${app}/${accountId}`, "_blank", "width=1100,height=800"); if (!w) window.open(`/ledger/${app}/${accountId}`, "_blank"); }}>
                <ExternalLink size={14} /> Pop out
              </Button>
            </>
          )}
        </div>
      </div>
      {isCredit && payOpen && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-3" data-testid="ledger-pay-panel">
          <span className="text-[11px] font-bold uppercase tracking-wide text-[var(--primary)]">{tp("Pay card", lang)}</span>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide opacity-55">{tp("Amount", lang)}</span>
            <input type="number" step="any" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="0.00" className={`${field} w-32`} data-testid="ledger-pay-amount" />
          </div>
          <button type="button" onClick={() => setPayAmount(String(data.account.min_payment || 0))} className="rounded border border-black/15 px-2 py-1 text-xs dark:border-white/20" data-testid="ledger-pay-min">{tp("Minimum", lang)} ({money(data.account.min_payment)})</button>
          <button type="button" onClick={() => setPayAmount(String(owed))} className="rounded border border-black/15 px-2 py-1 text-xs dark:border-white/20" data-testid="ledger-pay-full">{tp("Full", lang)} ({money(owed)})</button>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide opacity-55">{TL("From pool (optional)")}</span>
            <select value={paySource} onChange={(e) => setPaySource(e.target.value)} className={`${field} w-56`} data-testid="ledger-pay-source">
              <option value="">{tp("— External / not tracked —", lang)}</option>
              {accounts.filter((a) => a.app === "currency" && a.id !== accountId).map((a) => (
                <option key={a.id} value={a.id}>Currencida: {a.name}</option>
              ))}
            </select>
          </div>
          <button type="button" onClick={payCard} className="rounded bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-white lift" data-testid="ledger-pay-submit">{tp("Apply payment", lang)}</button>
          <button type="button" onClick={() => setPayOpen(false)} className="rounded border border-black/15 px-2 py-1.5 text-xs dark:border-white/20" data-testid="ledger-pay-cancel">{tp("Cancel", lang)}</button>
        </div>
      )}
      {msg && <div className="text-xs text-[#DA1E28]" data-testid="ledger-msg">{msg}</div>}

      <div className="flex flex-wrap items-center gap-3 rounded-md bg-black/[0.03] px-2 py-1.5 text-xs dark:bg-white/[0.04]" data-testid="ledger-fees">
        <span className="font-medium opacity-70">{tp("Fees this period", lang)}</span>
        <select value={feePeriod} onChange={(e) => setFeePeriod(e.target.value)} className={`${field} py-0.5`} data-testid="ledger-fees-period">
          {FEE_PERIODS.map(([v, l]) => <option key={v} value={v}>{tp(l, lang)}</option>)}
        </select>
        {FEE_FAMILIES.map(([k, l]) => (
          <span key={k} className="inline-flex items-center gap-1" data-testid={`ledger-fees-${k}`}>
            <span className="opacity-60">{tp(l, lang)}:</span><b>{money(fees[k])}</b>
          </span>
        ))}
        <span className="ms-auto inline-flex items-center gap-1">
          <span className="opacity-60">{tp("Total charges:", lang)}</span>
          <b className="text-[var(--primary)]" data-testid="ledger-fees-total">{money(feeTotal)}</b>
        </span>
      </div>

      {app === "currency" && (
        <div className="rounded-lg border border-black/10 p-2 dark:border-white/10" data-testid="ledger-statements">
          <CurrencidaStatements scope={accountId} accountName={regName} />
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="min-w-[900px] space-y-1">
          {feeFilter && <div className="mb-1 flex items-center gap-2 text-xs text-[var(--primary)]" data-testid="fee-filter-banner">{tp("Showing", lang)} “{feeFilter}” {tp("fees only", lang)} <button className="underline" onClick={() => setFeeFilter("")}>{tp("clear", lang)}</button></div>}
          {data.entries.filter((e) => !feeFilter || e.source_entry_id === `fee:${feeFilter}`).map((e) => (
            <div key={e.id} className="rounded-md border border-black/5 dark:border-white/5 px-2 py-1.5 text-sm" data-testid={`ledger-entry-${e.id}`}>
              <div className="grid grid-cols-[110px_110px_1fr_110px_110px_120px_120px_70px] items-center gap-2">
                <div>{e.date}</div>
                <div className="truncate">{e.ref || "—"}</div>
                <div className="flex items-center gap-1 truncate">
                  <button onClick={() => toggleReconcile(e)} title={tp("Cleared/Reconciled", lang)} data-testid={`ledger-reconcile-${e.id}`}
                    className={`flex h-4 w-4 items-center justify-center rounded border ${e.reconciled ? "bg-[#0E9F6E] text-white border-[#0E9F6E]" : "border-black/30 dark:border-white/30"}`}>
                    {e.reconciled && <Check size={11} />}
                  </button>
                  <span className="truncate">{e.description || "—"}</span>
                  {e.auto && <span title={`Auto-posted (${e.rule_type || "rule"})`} data-testid={`ledger-auto-${e.id}`} className="rounded bg-[var(--primary)]/15 px-1 text-[10px] font-medium text-[var(--primary)]">{tp("auto", lang)}</span>}
                  {e.crypto && <span title={TL("Crypto purchase (value only, no cash moves)")} data-testid={`ledger-crypto-${e.id}`} className="rounded bg-[#C99415]/20 px-1 text-[10px] font-medium text-[#8a6a00] dark:text-[#e6c66b]">◈ {num(e.crypto_units).toLocaleString(undefined, { maximumFractionDigits: 8 })} {e.crypto_symbol}</span>}
                  {e.context_on && <span title={tp("Has reviewer note", lang)} className="text-[var(--primary)]">*</span>}
                </div>
                <div className="text-[#DA1E28]">{e.debit ? money(e.debit) : "—"}</div>
                <div className="text-[#0E9F6E]">{e.credit ? money(e.credit) : "—"}</div>
                <div>{e.charge ? `${money(e.charge)}${e.charge_period ? ` (${e.charge_period})` : ""}` : "—"}</div>
                <div className="font-medium">{money(e.balance)}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => edit(e)} data-testid={`ledger-edit-${e.id}`} className="opacity-60 hover:opacity-100"><Pencil size={13} /></button>
                  <button onClick={() => del(e.id)} data-testid={`ledger-del-${e.id}`} className="text-[#DA1E28] hover:opacity-70"><Trash2 size={13} /></button>
                </div>
              </div>
              {(e.details_on || e.counterparty || e.context_note) && (
                <div className="mt-1 grid grid-cols-[110px_110px_1fr_240px_1fr] gap-2 text-xs opacity-70">
                  <div></div><div></div>
                  <div className="truncate">{e.details_on ? (e.details || <span className="opacity-40">{tp("auto details", lang)}</span>) : ""}</div>
                  <div className="truncate">{e.counterparty ? `↔ ${e.counterparty}` : ""}</div>
                  <div className="truncate">{e.context_on ? e.context_note : ""}</div>
                </div>
              )}
            </div>
          ))}
          {data.entries.length === 0 && <div className="py-6 text-center text-sm opacity-50" data-testid="ledger-empty">{tp("No entries yet. Add the first one below.", lang)}</div>}
        </div>
      </div>

      {/* Add / edit entry */}
      <div className="rounded-lg border border-black/10 dark:border-white/10 p-2 space-y-1" data-testid="ledger-form">
        {editId && <div className="text-xs font-medium text-[var(--primary)]">{tp("Editing entry", lang)}</div>}
        <div className="text-sm font-bold uppercase leading-none tracking-wide" data-testid="ledger-new-entry-label">{tp("New Entry", lang)}</div>
        {app === "currency" && !isCredit && (
          <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-black/10 p-2 dark:border-white/10" data-testid="ledger-overdraft">
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#0E9F6E]">{tp("Available Overdraft", lang)}</span>
            <input type="number" step="any" placeholder={tp("Overdraft amount", lang)} value={odLimit} onChange={(e) => setOdLimit(e.target.value)} className={`${field} w-40`} data-testid="od-amount" />
            <input type="date" value={odDate} onChange={(e) => setOdDate(e.target.value)} className={`${field} w-32`} data-testid="od-date" />
            <button type="button" onClick={() => setOdAuto((a) => !a)} className={`rounded-full px-2.5 py-1 text-xs font-medium ${odAuto ? "bg-[#0E9F6E] text-white" : "border border-black/15 dark:border-white/20"}`} data-testid="od-toggle">{odAuto ? tp("Auto draw", lang) : tp("Manual", lang)}</button>
            <span className="text-xs whitespace-nowrap"><span className="opacity-55">{tp("Balance used:", lang)}</span> <b className="text-[#DA1E28]" data-testid="od-used">{money(odUsed)}</b></span>
            <span className="text-xs whitespace-nowrap"><span className="opacity-55">{tp("Net Available:", lang)}</span> <b className={odNet < 0 ? "text-[#DA1E28]" : "text-[#0E9F6E]"} data-testid="od-net">{money(odNet)}</b></span>
            <div className="ms-auto flex items-center gap-1">
              <button type="button" onClick={applyOverdraft} className="rounded bg-[var(--primary)] px-2 py-1 text-[10px] font-medium text-white" data-testid="od-apply">{tp("Apply", lang)}</button>
              <button type="button" onClick={undoOverdraft} className="rounded border border-black/15 px-2 py-1 text-[10px] dark:border-white/20" data-testid="od-undo">{tp("Undo", lang)}</button>
            </div>
          </div>
        )}
        {intRow("charged", intCharged, setIC, grossCharged, "Gross Interest Charged", "Apply to Balance")}
        <div className="pt-2 flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-0.5">
            <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={field} data-testid="ledger-f-date" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium uppercase tracking-wide opacity-55">{tp("Cheque/Wire", lang)}</span>
            <input maxLength={15} value={form.ref} onChange={(e) => set("ref", e.target.value)} className={`${field} w-32`} data-testid="ledger-f-ref" />
          </div>
          <div className="flex w-64 flex-col gap-0.5">
            <span className="text-[10px] font-medium uppercase tracking-wide opacity-55">{tp("Line 1 Description", lang)}</span>
            <select value={form.desc_type}
              onChange={(e) => e.target.value === "__add" ? addDescOption() : set("desc_type", e.target.value)}
              className={`${field} w-full`} data-testid="ledger-f-desc-type">
              <option value="">{tp("Select…", lang)}</option>
              {descOptions.map((o) => <option key={o} value={o}>{tp(o, lang)}</option>)}
              <option value="__add">{tp("➕ Add more…", lang)}</option>
            </select>
            <input placeholder={tp("Description", lang)} maxLength={50} value={form.description} onChange={(e) => set("description", e.target.value)} className={`${field} w-full`} data-testid="ledger-f-desc" />
          </div>
          {isBox && (
            <label className="flex items-center gap-1 text-xs font-medium" data-testid="ledger-f-crypto-toggle" title={tp("Log a purchase paid with crypto — value enters/leaves, no cash moves", lang)}>
              <input type="checkbox" checked={form.crypto} onChange={(e) => set("crypto", e.target.checked)} /> Crypto
            </label>
          )}
          {form.crypto ? (
            <>
              <select value={form.crypto_direction} onChange={(e) => set("crypto_direction", e.target.value)} className={field} data-testid="ledger-f-crypto-dir">
                <option value="buy">{TL("Buy with crypto (value leaves)")}</option>
                <option value="receive">{TL("Receive / sell for crypto (value enters)")}</option>
              </select>
              <input placeholder={tp("Symbol", lang)} maxLength={12} value={form.crypto_symbol} onChange={(e) => set("crypto_symbol", e.target.value.toUpperCase())} className={`${field} w-24`} data-testid="ledger-f-crypto-symbol" />
              <input type="number" step="any" placeholder={tp("Units", lang)} value={form.crypto_units} onChange={(e) => set("crypto_units", e.target.value)} className={`${field} w-24`} data-testid="ledger-f-crypto-units" />
              <input type="number" step="any" placeholder={tp("Value", lang)} value={form.crypto_value} onChange={(e) => set("crypto_value", e.target.value)} className={`${field} w-52`} data-testid="ledger-f-crypto-value" />
              {cryptoPx != null && (
                <button type="button" onClick={() => set("crypto_value", (cryptoPx * num(form.crypto_units)).toFixed(2))} className="text-xs text-[var(--primary)] underline" data-testid="ledger-f-crypto-fill">
                  ${cryptoPx.toLocaleString()}/unit → use
                </button>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium uppercase tracking-wide opacity-55">{isCredit ? tp("Purchase/Debit", lang) : tp("Payment/Debit", lang)}</span>
                <input type="number" step="any" placeholder={tp("Debit", lang)} value={form.debit} onChange={(e) => set("debit", e.target.value)} className={`${field} w-52`} data-testid="ledger-f-debit" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium uppercase tracking-wide opacity-55">{isCredit ? tp("Payment/Credit", lang) : tp("Deposit/Credit", lang)}</span>
                <input type="number" step="any" placeholder={tp("Credit", lang)} value={form.credit} onChange={(e) => set("credit", e.target.value)} className={`${field} w-52`} data-testid="ledger-f-credit" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium uppercase tracking-wide opacity-55">{tp("Bank charges", lang)}</span>
                <div className="flex items-center gap-1">
                  <input type="number" step="any" placeholder={tp("Account package", lang)} value={form.package_amount} onChange={(e) => set("package_amount", e.target.value)} className={`${field} w-52`} data-testid="ledger-f-package" />
                  <select value={form.package_period} onChange={(e) => set("package_period", e.target.value)} className={field} data-testid="ledger-f-package-period" title="Package cadence — set once on the first entry; stays until changed">
                    {PACKAGE_PERIODS.map(([v, l]) => <option key={v} value={v}>{tp(l, lang)}</option>)}
                  </select>
                  <span className="ms-2 w-52 text-[10px] font-medium uppercase tracking-wide opacity-55">{tp("Balance", lang)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <input type="number" step="any" placeholder={tp("Bank Charges", lang)} value={form.charge} onChange={(e) => set("charge", e.target.value)} className={`${field} w-52`} data-testid="ledger-f-charge" />
                  <select value={form.trigger} onChange={(e) => set("trigger", e.target.value)} className={`${field} w-40`} data-testid="ledger-f-trigger" title={tp("Bank-charge trigger", lang)}>
                    {BANK_TRIGGERS.map(([v, l]) => <option key={v} value={v}>{tp(l, lang)}</option>)}
                  </select>
                  <input readOnly value={money(num(data.balance) + num(form.credit) - num(form.debit) - num(form.charge))} className={`${field} ms-2 w-52 opacity-70`} data-testid="ledger-f-balance" />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1 text-xs" data-testid="ledger-f-details-on">
            <input type="checkbox" checked={form.details_on} onChange={(e) => set("details_on", e.target.checked)} /> Auto details
          </label>
          {form.details_on && (
            <input placeholder={tp("Transaction details", lang)} value={form.details} onChange={(e) => set("details", e.target.value)} className={`${field} min-w-[180px] flex-1`} data-testid="ledger-f-details" />
          )}
          <button type="button" onClick={() => { if (editId) { del(editId); reset(); } else { reset(); } }}
            title={editId ? tp("Delete this transaction", lang) : tp("Clear this entry", lang)} data-testid="ledger-f-delete"
            className="rounded-md border border-[#DA1E28]/40 px-2 py-1.5 text-[#DA1E28] hover:bg-[#DA1E28]/10"><Trash2 size={15} /></button>
          <input placeholder={tp("Counterparty/Vendor", lang)} value={form.counterparty} onChange={(e) => set("counterparty", e.target.value)} className={`${field} min-w-[200px]`} data-testid="ledger-f-counterparty" />
        </div>
        <div className="rounded-lg border border-black/10 p-2 dark:border-white/10" data-testid="ledger-levy">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide opacity-55">{tp("Levy Rules — bank charges", lang)}</div>
          <div className="flex flex-nowrap gap-y-3 overflow-x-auto">
            {LEVY_SECTIONS.map((sec, i) => (
              <div key={sec.key} className={`flex shrink-0 items-start gap-2 px-2 ${i > 0 ? "border-double border-l-[3px] border-black/25 dark:border-white/25" : ""}`} data-testid={`levy-sec-${i}`}>
                <input type="checkbox" checked={levy[i].on} onChange={(e) => setLevyCell(i, "on", e.target.checked)} className="mt-1 h-4 w-4" data-testid={`levy-on-${i}`} />
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <input placeholder={tp("Item Levy", lang)} value={levy[i].item} onChange={(e) => setLevyCell(i, "item", e.target.value)} className={`${field} w-24`} data-testid={`levy-item-${i}`} />
                    <input type="number" step="any" placeholder={tp("Amount", lang)} value={levy[i].amount} onChange={(e) => setLevyCell(i, "amount", e.target.value)} className={`${field} w-24`} data-testid={`levy-amt-${i}`} />
                    <span className="flex flex-col text-center text-[10px] leading-tight opacity-55"><span>{tp(sec.top, lang)}</span><span>{tp(sec.bot, lang)}</span></span>
                  </div>
                  <div className="flex items-center gap-1">
                    <input placeholder={tp("Tax label", lang)} value={levy[i].tax_label} onChange={(e) => setLevyCell(i, "tax_label", e.target.value)} className={`${field} w-24`} data-testid={`levy-tax-label-${i}`} />
                    <input type="number" step="any" placeholder={levy[i].tax_mode === "pct" ? "%" : tp("Amount", lang)} value={levy[i].tax_val} onChange={(e) => setLevyCell(i, "tax_val", e.target.value)} className={`${field} w-20`} data-testid={`levy-tax-val-${i}`} />
                    <button type="button" onClick={() => setLevyCell(i, "tax_mode", levy[i].tax_mode === "pct" ? "amt" : "pct")} className="rounded border border-black/15 px-1.5 py-0.5 text-[10px] dark:border-white/20" data-testid={`levy-tax-mode-${i}`}>{levy[i].tax_mode === "pct" ? "%" : "$"}</button>
                  </div>
                  <div className="flex items-center gap-1">
                    <input placeholder={tp("In house charge / Tracker", lang)} value={levy[i].inhouse} onChange={(e) => setLevyCell(i, "inhouse", e.target.value)} className={`${field} w-48`} data-testid={`levy-inhouse-${i}`} />
                    <input placeholder={tp("Entry", lang)} value={levy[i].entry} onChange={(e) => setLevyCell(i, "entry", e.target.value)} className={`${field} w-16`} data-testid={`levy-entry-${i}`} />
                  </div>
                </div>
              </div>
            ))}
            <div className="flex shrink-0 items-start gap-2 border-double border-l-[3px] border-black/25 px-2 dark:border-white/25" data-testid="levy-sec-5">
              <div className="w-80 space-y-1">
                <div className="flex items-center gap-1">
                  <button type="button" onClick={applyFee} className="rounded bg-[var(--primary)] px-1.5 py-0.5 text-[10px] text-white" data-testid="levy5-apply">{tp("Apply", lang)}</button>
                  <button type="button" onClick={undoSec5} className="rounded border border-black/15 px-1.5 py-0.5 text-[10px] dark:border-white/20" data-testid="levy5-delete">{tp("Delete", lang)}</button>
                  <select value={sec5.fee} onChange={(e) => setSec5Cell("fee", e.target.value)} className={`${field} flex-1`} data-testid="levy5-fee">
                    <option value="">{tp("Fee type…", lang)}</option>
                    {FEE_TYPES.map((f) => <option key={f} value={f}>{tp(f, lang)}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <input type="number" step="any" placeholder={tp("Item Levy", lang)} value={sec5.item_amount} onChange={(e) => setSec5Cell("item_amount", e.target.value)} className={`${field} w-20`} data-testid="levy5-item" />
                  <input placeholder={tp("Tax label", lang)} value={sec5.tax_label} onChange={(e) => setSec5Cell("tax_label", e.target.value)} className={`${field} flex-1`} data-testid="levy5-tax-label" />
                  <input type="number" step="any" placeholder={sec5.tax_mode === "pct" ? "%" : tp("Amount", lang)} value={sec5.tax_val} onChange={(e) => setSec5Cell("tax_val", e.target.value)} className={`${field} w-16`} data-testid="levy5-tax-val" />
                  <button type="button" onClick={() => setSec5Cell("tax_mode", sec5.tax_mode === "pct" ? "amt" : "pct")} className="rounded border border-black/15 px-1.5 py-0.5 text-[10px] dark:border-white/20" data-testid="levy5-tax-mode">{sec5.tax_mode === "pct" ? "%" : "$"}</button>
                </div>
                <div className="flex items-center gap-1">
                  <input type="date" value={sec5.date} onChange={(e) => setSec5Cell("date", e.target.value)} className={`${field} w-28`} data-testid="levy5-date" />
                  <button type="button" onClick={findFee} className={`flex-1 rounded border px-1.5 py-0.5 text-[10px] ${feeFilter ? "border-[var(--primary)] text-[var(--primary)]" : "border-black/15 dark:border-white/20"}`} data-testid="levy5-find-all">{tp("Find All", lang)}</button>
                  <button type="button" onClick={deleteAllFee} className="flex-1 rounded border border-black/15 px-1.5 py-0.5 text-[10px] dark:border-white/20" data-testid="levy5-delete-all">{tp("Delete All", lang)}</button>
                  <button type="button" onClick={undoSec5} className="flex-1 rounded border border-black/15 px-1.5 py-0.5 text-[10px] dark:border-white/20" data-testid="levy5-undo">{tp("Undo", lang)}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {!isCredit && intRow("earned", intEarned, setIE, netEarned, "Net Interest Earned", "Add to Balance")}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1 text-xs" data-testid="ledger-f-context-on">
            <input type="checkbox" checked={form.context_on} onChange={(e) => set("context_on", e.target.checked)} /> Add reviewer note
          </label>
          {form.context_on && (
            <input placeholder={tp("Extra context / reconciling notes", lang)} value={form.context_note} onChange={(e) => set("context_note", e.target.value)} className={`${field} min-w-[240px] flex-1`} data-testid="ledger-f-context" />
          )}
          <label className="flex items-center gap-1 text-xs" data-testid="ledger-f-reconciled">
            <input type="checkbox" checked={form.reconciled} onChange={(e) => set("reconciled", e.target.checked)} /> Reconciled
          </label>
          <div className="ms-auto flex items-center gap-2">
            {editId && <Button variant="ghost" onClick={reset} data-testid="ledger-cancel"><X size={14} /> {tp("Cancel", lang)}</Button>}
            <Button onClick={save} disabled={busy} data-testid="ledger-save">{editId ? <Save size={14} /> : <Plus size={14} />} {editId ? tp("Update", lang) : tp("Add entry", lang)}</Button>
          </div>
        </div>
      </div>

      <LedgerRules app={app} accountId={accountId} open={rulesOpen} onClose={() => setRulesOpen(false)} onSaved={(d) => d && setData(d)} />
    </div>
  );
}
